import { TableData } from '@anytag/shared-types';
import { Button, TableV2, BaseModal, SelectionBar } from '@anytag/shared-atoms';
import {
  useToggleState,
  useQueryHelper,
  useSelectItem,
  useSelectItemIds,
  useDeepCompareEffect
} from '@anytag/shared-hooks';
import {
  namedOperations,
  AutoManagedPayoutPeriodInput,
  AutoManagedCommissionInfluencer,
  useApproveAutoManagedPayoutMutation
} from '@anytag/graphql';
import { useAutoManagedCommissionsFilter } from '../AutoManagedCommissionsFilter';
import { useAutoManagedCommissionsInfluencersTable } from './hooks';

export interface AutoManagedCommissionsListProps extends TableData<AutoManagedCommissionInfluencer> {
  autoManagedCampaignId: number;
}
export const AutoManagedCommissionsList = ({
  loading,
  data = [],
  autoManagedCampaignId
}: AutoManagedCommissionsListProps) => {
  const confirmModal = useToggleState();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { filter } = useAutoManagedCommissionsFilter();
  const { selectedItem, resetSelectedItem, setSelectedItem } = useSelectItem();
  const { selectItem, selectedItemIds, hasAllItemsSelected, setSelectedItemIds, toggleSelectAllItems } =
    useSelectItemIds({ allItemIds: data.map(({ influencerId }) => influencerId) });
  const { columns } = useAutoManagedCommissionsInfluencersTable({
    data,
    selectItem,
    selectedItemIds,
    hasAllItemsSelected,
    toggleSelectAllItems,
    onApprove: (influencer) => {
      setSelectedItem(influencer);
      confirmModal.open();
    }
  });
  const { callApproveAutoManagedPayout, loading: approving } = useApproveAutoManagedPayoutMutation({
    refetchQueries: [namedOperations.Query.AutoManagedCommissionInfluencers]
  });

  useDeepCompareEffect(() => {
    resetSelectedItems();
  }, [data]);

  const resetSelectedItems = () => {
    resetSelectedItem();
    setSelectedItemIds([]);
  };

  const handleCloseModal = () => {
    resetSelectedItems();
    confirmModal.close();
  };

  const handleApprove = async () => {
    try {
      const allSelectedItemIds = selectedItem ? [selectedItem.id] : selectedItemIds;
      await callApproveAutoManagedPayout({
        variables: {
          input: {
            id: autoManagedCampaignId,
            coveragePeriods: data.reduce<AutoManagedPayoutPeriodInput[]>(
              (acc, curr) =>
                allSelectedItemIds.includes(curr.influencerId)
                  ? [...acc, { fixedDate: curr.fixedDate, postId: curr.postId }]
                  : acc,
              []
            )
          }
        }
      });
      handleCloseModal();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <>
      <div css={{ position: 'relative' }}>
        {selectedItemIds.length > 0 ? (
          <SelectionBar
            disabled={approving}
            selectAllProps={{ onClick: toggleSelectAllItems }}
            checkboxProps={{
              disabled: approving,
              onClick: toggleSelectAllItems,
              indeterminate: !hasAllItemsSelected,
              label: t('Selected', { count: selectedItemIds.length })
            }}
          >
            <Button
              onClick={confirmModal.open}
              title={t('Button.Approve Commission')}
              css={{ padding: '0 16px', marginLeft: '24px' }}
            />
          </SelectionBar>
        ) : null}
      </div>

      <TableV2
        loading={loading}
        data={data}
        columns={columns}
        rowHeight="64px"
        empty={{ description: filter.keyword ? undefined : t('Nobody joined this campaign yet') }}
        noTopBorderRadius
      />

      <BaseModal
        open={confirmModal.status}
        onClose={handleCloseModal}
        title={t('Are you sure to approve the payout?')}
        description={
          <>
            {t('Creators will be able to withdraw commission fee after you approved')}
            <br />
            {t('This operation cannot be canceled once you click “approve”')}
          </>
        }
        footer={{
          cancelButton: { onClick: handleCloseModal },
          submitButton: { loading: approving, onClick: handleApprove, title: t('Button.Approve') }
        }}
      />
    </>
  );
};
